import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import type { User } from '@supabase/supabase-js';
import type { Profile } from '../../types/profile';
import { Spinner } from '../../components/Spinner';
import { evaluatePasswordStrength } from '../../lib/passwordStrength';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { cancelAccountDeletion, requestAccountDeletion } from '../../server/api';
import { cardClass, inputClass, labelClass, lockedFieldShellStyle, settingsHelperMutedStyle } from './styles';
import { IconLock14 } from './SecurityFieldIcons';

const LEAVING_REASONS = [
  'Prefer not to say',
  'Too expensive',
  'Missing features I need',
  'Switching to another product',
  'Business closed',
  'Security concerns',
  'Other',
] as const;

const modalInputBase: React.CSSProperties = {
  width: '100%',
  backgroundColor: 'var(--bg-card)',
  border: '1px solid var(--border-color)',
  borderRadius: '8px',
  padding: '10px 12px',
  fontSize: '14px',
  color: 'var(--text-primary)',
  outline: 'none',
};

function browserLabel(): string {
  if (typeof navigator === 'undefined') return 'Web browser';
  const ua = navigator.userAgent;
  if (/Edg\//.test(ua)) return 'Microsoft Edge';
  if (/OPR\//.test(ua) || /Opera/.test(ua)) return 'Opera';
  if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) return 'Chrome';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return 'Safari';
  return 'Web browser';
}

function approximateLocation(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unknown region';
  } catch {
    return 'Unknown region';
  }
}

function validateNewPassword(password: string): string | null {
  if (!password) return 'Create a password.';
  if (password.length < 8) return 'Use at least 8 characters.';
  if (!/[a-z]/.test(password) || !/[A-Z]/.test(password))
    return 'Include upper and lower case letters.';
  if (!/\d/.test(password)) return 'Include at least one number.';
  return null;
}

function daysUntil(isoDate: string): number {
  const diff = new Date(isoDate).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function formatDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function WarningTriangleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width={24}
      height={24}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M12 3.5L2.5 20h19L12 3.5z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M12 9v5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <circle cx="12" cy="17.5" r="1" fill="currentColor" />
    </svg>
  );
}

type Props = {
  currentUser: User;
  userProfile: Profile | null;
  onToast: (message: string, variant: 'success' | 'error') => void;
};

export function AccountSecurityTab({ currentUser, userProfile, onToast }: Props) {
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();
  const deleteModalRef = useRef<HTMLDivElement>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMsg, setPasswordMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [signingOutOthers, setSigningOutOthers] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteBusinessConfirm, setDeleteBusinessConfirm] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteReason, setDeleteReason] = useState<string>(LEAVING_REASONS[0]);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [businessFieldError, setBusinessFieldError] = useState(false);
  const [passwordFieldError, setPasswordFieldError] = useState(false);

  const [cancelDeletionSubmitting, setCancelDeletionSubmitting] = useState(false);
  const [cancelDeletionError, setCancelDeletionError] = useState<string | null>(null);
  const [cancelDeletionSuccessMessage, setCancelDeletionSuccessMessage] = useState<string | null>(
    null
  );

  const strength = evaluatePasswordStrength(newPassword);
  const email = currentUser.email ?? '';
  const registeredBusinessName = userProfile?.business_name?.trim() ?? '';

  const canSubmitDeletion =
    deleteBusinessConfirm.trim().length > 0 && deletePassword.length > 0 && !deleteSubmitting;

  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setDeleteBusinessConfirm('');
    setDeletePassword('');
    setDeleteReason(LEAVING_REASONS[0]);
    setDeleteError(null);
    setBusinessFieldError(false);
    setPasswordFieldError(false);
    setDeleteSubmitting(false);
  }, []);

  useEffect(() => {
    if (!deleteModalOpen) return;

    const modalRoot = deleteModalRef.current;
    if (!modalRoot) return;
    const root: HTMLElement = modalRoot;

    const selector =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function getFocusables(): HTMLElement[] {
      return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
        (el) => !el.hasAttribute('disabled')
      );
    }

    const focusables = getFocusables();
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    window.setTimeout(() => {
      if (deleteModalRef.current) first?.focus();
    }, 0);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeDeleteModal();
        return;
      }
      if (e.key !== 'Tab' || focusables.length === 0) return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [deleteModalOpen, closeDeleteModal]);

  async function handleUpdatePassword(e: React.FormEvent) {
    e.preventDefault();
    setPasswordMsg(null);

    if (!currentPassword) {
      setPasswordMsg({ type: 'err', text: 'Enter your current password.' });
      onToast('Enter your current password.', 'error');
      return;
    }
    const npErr = validateNewPassword(newPassword);
    if (npErr) {
      setPasswordMsg({ type: 'err', text: npErr });
      onToast(npErr, 'error');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'err', text: 'New passwords do not match.' });
      onToast('New passwords do not match.', 'error');
      return;
    }

    setUpdatingPassword(true);
    const { error: signErr } = await supabase.auth.signInWithPassword({
      email,
      password: currentPassword,
    });

    if (signErr) {
      setUpdatingPassword(false);
      setPasswordMsg({ type: 'err', text: 'Current password is incorrect.' });
      onToast('Current password is incorrect.', 'error');
      return;
    }

    const { error: updErr } = await supabase.auth.updateUser({ password: newPassword });
    setUpdatingPassword(false);

    if (updErr) {
      setPasswordMsg({ type: 'err', text: updErr.message });
      onToast(updErr.message, 'error');
      return;
    }

    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordMsg({ type: 'ok', text: 'Your password was updated successfully.' });
    onToast('Changes saved', 'success');
  }

  async function handleSignOutOthers() {
    setSigningOutOthers(true);
    const { error } = await supabase.auth.signOut({ scope: 'others' });
    setSigningOutOthers(false);
    if (error) {
      onToast(error.message || 'Could not sign out other sessions.', 'error');
      return;
    }
    onToast('Changes saved', 'success');
  }

  function openDeleteModal() {
    setCancelDeletionSuccessMessage(null);
    setDeleteBusinessConfirm('');
    setDeletePassword('');
    setDeleteReason(LEAVING_REASONS[0]);
    setDeleteError(null);
    setBusinessFieldError(false);
    setPasswordFieldError(false);
    setDeleteModalOpen(true);
  }

  async function handleScheduleDeletion(e: React.FormEvent) {
    e.preventDefault();
    setDeleteError(null);
    setBusinessFieldError(false);
    setPasswordFieldError(false);
    if (!canSubmitDeletion) return;

    setDeleteSubmitting(true);
    const result = await requestAccountDeletion({
      password: deletePassword,
      businessName: deleteBusinessConfirm,
      reason: deleteReason,
    });
    setDeleteSubmitting(false);

    if (!result.ok) {
      if (result.status === 400 && result.error === 'Business name does not match our records') {
        setDeleteError('Business name does not match. Please type it exactly as registered.');
        setBusinessFieldError(true);
      } else if (result.status === 401 && result.error === 'Incorrect password') {
        setDeleteError('Incorrect password. Please try again.');
        setPasswordFieldError(true);
      } else {
        setDeleteError(result.error);
      }
      return;
    }

    closeDeleteModal();
    await supabase.auth.signOut();
    navigate('/login?message=deletion_scheduled', { replace: true });
  }

  async function handleCancelDeletion() {
    setCancelDeletionError(null);
    setCancelDeletionSubmitting(true);
    const result = await cancelAccountDeletion();
    setCancelDeletionSubmitting(false);
    if (!result.ok) {
      setCancelDeletionError(result.error);
      return;
    }
    await refreshProfile();
    setCancelDeletionSuccessMessage(
      'Your account deletion has been cancelled. Your account is fully active.'
    );
  }

  const dangerZoneCardStyle: React.CSSProperties = {
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border-color)',
    borderLeft: '4px solid var(--accent-danger)',
    borderRadius: '1rem',
    padding: '24px',
  };

  const deleteAccountBtnStyle: React.CSSProperties = {
    border: '1.5px solid var(--accent-danger)',
    backgroundColor: 'transparent',
    color: 'var(--accent-danger)',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 500,
    transition: 'all 0.2s ease',
    width: '100%',
  };

  const scheduledDeletionCardStyle: React.CSSProperties = {
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border-color)',
    borderLeft: '4px solid var(--accent-amber)',
    borderRadius: '1rem',
    padding: '24px',
  };

  const cancelDeletionBtnStyle: React.CSSProperties = {
    background: 'var(--accent-green)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.15s ease',
    width: '100%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  };

  const modalOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    zIndex: 9999,
    background: 'var(--modal-overlay-bg)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
  };

  const modalPanelStyle: React.CSSProperties = {
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border-color)',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '480px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: 'var(--modal-panel-shadow)',
  };

  const deleteModalPortal =
    deleteModalOpen && typeof document !== 'undefined'
      ? createPortal(
          <div style={modalOverlayStyle} role="presentation">
            <div
              ref={deleteModalRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="schedule-deletion-title"
              style={modalPanelStyle}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  padding: '20px 24px 16px',
                  borderBottom: '1px solid var(--border-color)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <span style={{ color: 'var(--accent-danger)', flexShrink: 0 }}>
                  <WarningTriangleIcon />
                </span>
                <h2
                  id="schedule-deletion-title"
                  style={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: 'var(--text-primary)',
                    margin: 0,
                  }}
                >
                  Delete Your Account
                </h2>
              </div>

              <form onSubmit={(e) => void handleScheduleDeletion(e)} noValidate>
                <div style={{ padding: '24px' }}>
                  <div
                    style={{
                      background: 'color-mix(in srgb, var(--accent-danger) 10%, transparent)',
                      border: '1px solid color-mix(in srgb, var(--accent-danger) 30%, transparent)',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      color: 'var(--accent-danger)',
                      fontSize: '14px',
                      lineHeight: 1.5,
                      marginBottom: '20px',
                    }}
                  >
                    ⚠ This will schedule your account for permanent deletion in 30 days. All your
                    data will be permanently destroyed and cannot be recovered. You may cancel within
                    30 days by logging back in.
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label
                      htmlFor="delete-reason"
                      style={{
                        display: 'block',
                        fontSize: '13px',
                        fontWeight: 500,
                        color: 'var(--text-secondary)',
                        marginBottom: '6px',
                      }}
                    >
                      Reason for leaving
                    </label>
                    <select
                      id="delete-reason"
                      value={deleteReason}
                      onChange={(e) => setDeleteReason(e.target.value)}
                      style={{
                        ...modalInputBase,
                        cursor: 'pointer',
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = 'var(--accent-green)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--border-color)';
                      }}
                    >
                      {LEAVING_REASONS.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label
                      htmlFor="delete-business-confirm"
                      style={{
                        display: 'block',
                        fontSize: '13px',
                        fontWeight: 500,
                        color: 'var(--text-secondary)',
                        marginBottom: '6px',
                      }}
                    >
                      Type your business name to confirm
                    </label>
                    <p
                      style={{
                        fontSize: '12px',
                        color: 'var(--text-tertiary)',
                        fontStyle: 'italic',
                        margin: '0 0 8px',
                      }}
                    >
                      Exactly as registered: {registeredBusinessName || '—'}
                    </p>
                    <input
                      id="delete-business-confirm"
                      type="text"
                      value={deleteBusinessConfirm}
                      onChange={(e) => {
                        setDeleteBusinessConfirm(e.target.value);
                        setDeleteError(null);
                        setBusinessFieldError(false);
                      }}
                      style={{
                        ...modalInputBase,
                        borderColor: businessFieldError
                          ? 'var(--accent-danger)'
                          : 'var(--border-color)',
                      }}
                      autoComplete="off"
                      onFocus={(e) => {
                        if (!businessFieldError) e.target.style.borderColor = 'var(--accent-green)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = businessFieldError
                          ? 'var(--accent-danger)'
                          : 'var(--border-color)';
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: deleteError ? '16px' : 0 }}>
                    <label
                      htmlFor="delete-password-confirm"
                      style={{
                        display: 'block',
                        fontSize: '13px',
                        fontWeight: 500,
                        color: 'var(--text-secondary)',
                        marginBottom: '6px',
                      }}
                    >
                      Confirm your password
                    </label>
                    <input
                      id="delete-password-confirm"
                      type="password"
                      value={deletePassword}
                      onChange={(e) => {
                        setDeletePassword(e.target.value);
                        setDeleteError(null);
                        setPasswordFieldError(false);
                      }}
                      style={{
                        ...modalInputBase,
                        borderColor: passwordFieldError
                          ? 'var(--accent-danger)'
                          : 'var(--border-color)',
                      }}
                      autoComplete="current-password"
                      onFocus={(e) => {
                        if (!passwordFieldError) e.target.style.borderColor = 'var(--accent-green)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = passwordFieldError
                          ? 'var(--accent-danger)'
                          : 'var(--border-color)';
                      }}
                    />
                  </div>

                  {deleteError ? (
                    <div
                      role="alert"
                      style={{
                        background: 'color-mix(in srgb, var(--accent-danger) 10%, transparent)',
                        borderLeft: '3px solid var(--accent-danger)',
                        borderRadius: '4px',
                        padding: '10px 14px',
                        fontSize: '13px',
                        color: 'var(--accent-danger)',
                      }}
                    >
                      {deleteError}
                    </div>
                  ) : null}
                </div>

                <div
                  style={{
                    padding: '16px 24px 20px',
                    borderTop: '1px solid var(--border-color)',
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'flex-end',
                    flexWrap: 'wrap',
                  }}
                >
                  <button
                    type="button"
                    onClick={closeDeleteModal}
                    style={{
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--border-color)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'var(--bg-secondary)';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!canSubmitDeletion}
                    style={{
                      background: 'var(--accent-danger)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: !canSubmitDeletion ? 'not-allowed' : 'pointer',
                      opacity: !canSubmitDeletion ? 0.5 : 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    {deleteSubmitting ? (
                      <>
                        <Spinner className="h-4 w-4 text-white" />
                        Scheduling...
                      </>
                    ) : (
                      'Schedule Deletion'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )
      : null;

  const isPendingDeletion = userProfile?.status === 'pending_deletion';
  const deletionScheduledAt = userProfile?.deletion_scheduled_at;

  return (
    <div className="space-y-6">
      <div className={cardClass}>
        <h2 className="text-lg font-semibold text-foreground sm:text-xl">Keep Your Account Secure</h2>

        <section className="mt-8">
          <h3 className="text-base font-semibold text-foreground">Account email</h3>
          <div className="mt-4">
            <span className={labelClass}>Email address</span>
            <div
              style={lockedFieldShellStyle}
              className="mt-1.5 w-full"
              title="This field cannot be changed"
            >
              <span className="min-w-0 truncate text-sm">{email || '—'}</span>
              <span className="shrink-0" style={{ color: 'var(--text-tertiary)' }}>
                <IconLock14 />
              </span>
            </div>
            <p className="mt-1.5" style={settingsHelperMutedStyle}>
              To change your email address contact hello@lognsec.com. We will verify ownership of both
              addresses before making any changes.
            </p>
          </div>
        </section>

        <section className="mt-10 border-t border-border pt-10">
          <h3 className="text-base font-semibold text-foreground">Change password</h3>
          <form onSubmit={(e) => void handleUpdatePassword(e)} className="mt-4 space-y-4" noValidate>
            <div>
              <label htmlFor="current-password" className={labelClass}>
                Current Password
              </label>
              <input
                id="current-password"
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="new-password" className={labelClass}>
                New Password
              </label>
              <input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={inputClass}
              />
              {newPassword ? (
                <div className="mt-2">
                  <div className="mb-1 flex gap-1">
                    {[0, 1, 2, 3].map((i) => (
                      <span
                        key={i}
                        className={`h-1 flex-1 rounded-full ${
                          i < strength.score ? strength.segmentClass : 'bg-border'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-muted">{strength.label}</p>
                </div>
              ) : null}
            </div>
            <div>
              <label htmlFor="confirm-new-password" className={labelClass}>
                Confirm New Password
              </label>
              <input
                id="confirm-new-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={inputClass}
              />
            </div>
            {passwordMsg ? (
              <p
                className={`text-sm ${passwordMsg.type === 'ok' ? 'text-accent' : 'text-danger'}`}
                role="status"
              >
                {passwordMsg.text}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={updatingPassword}
              className="rounded-lg border border-accent/50 bg-accent/10 px-4 py-2.5 text-sm font-semibold text-accent transition hover:bg-accent/20 disabled:opacity-60"
            >
              {updatingPassword ? 'Updating…' : 'Update Password'}
            </button>
          </form>
        </section>

        <section className="mt-10 border-t border-border pt-10">
          <h3 className="text-base font-semibold text-foreground">Active Sessions</h3>
          <div className="mt-4 rounded-xl border border-border bg-navy/40 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">This device</p>
                <p className="mt-1 text-sm text-muted">
                  {browserLabel()} · {approximateLocation()}
                </p>
              </div>
              <span className="w-fit rounded-full bg-accent/15 px-2.5 py-1 text-xs font-semibold text-accent">
                Current Session
              </span>
            </div>
          </div>
          <button
            type="button"
            disabled={signingOutOthers}
            onClick={() => void handleSignOutOthers()}
            className="mt-4 w-full rounded-lg border border-border px-4 py-2.5 text-sm font-semibold text-foreground transition hover:bg-foreground/5 disabled:opacity-60 sm:w-auto"
          >
            {signingOutOthers ? 'Signing out…' : 'Log Out of All Other Devices'}
          </button>
        </section>
      </div>

      {isPendingDeletion ? (
        <div style={scheduledDeletionCardStyle}>
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 flex-1">
              <h3
                style={{
                  fontWeight: 600,
                  color: 'var(--accent-amber)',
                  fontSize: '16px',
                  margin: '0 0 8px',
                }}
              >
                Account Deletion Scheduled
              </h3>
              <div
                style={{
                  color: 'var(--text-secondary)',
                  fontSize: '14px',
                  lineHeight: 1.6,
                }}
              >
                {deletionScheduledAt ? (
                  <p style={{ margin: '0 0 8px' }}>
                    Your account is scheduled for permanent deletion on{' '}
                    {formatDate(deletionScheduledAt)} — in {daysUntil(deletionScheduledAt)} days.
                  </p>
                ) : (
                  <p style={{ margin: '0 0 8px' }}>
                    Your account is scheduled for permanent deletion.
                  </p>
                )}
                <p style={{ margin: 0 }}>
                  All your data will be permanently destroyed. Cancel now to keep your account.
                </p>
              </div>
              {cancelDeletionError ? (
                <div
                  role="alert"
                  style={{
                    marginTop: '12px',
                    background: 'color-mix(in srgb, var(--accent-danger) 10%, transparent)',
                    borderLeft: '3px solid var(--accent-danger)',
                    borderRadius: '4px',
                    padding: '10px 14px',
                    fontSize: '13px',
                    color: 'var(--accent-danger)',
                  }}
                >
                  {cancelDeletionError}
                </div>
              ) : null}
            </div>
            <div className="flex w-full shrink-0 lg:w-auto lg:justify-end lg:pt-0">
              <button
                type="button"
                disabled={cancelDeletionSubmitting}
                onClick={() => void handleCancelDeletion()}
                style={{
                  ...cancelDeletionBtnStyle,
                  opacity: cancelDeletionSubmitting ? 0.7 : 1,
                  cursor: cancelDeletionSubmitting ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={(e) => {
                  if (cancelDeletionSubmitting) return;
                  e.currentTarget.style.background = 'color-mix(in srgb, var(--accent-green) 82%, black)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--accent-green)';
                }}
              >
                {cancelDeletionSubmitting ? (
                  <>
                    <Spinner className="h-4 w-4 text-white" />
                    Cancelling…
                  </>
                ) : (
                  'Cancel Account Deletion'
                )}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div style={dangerZoneCardStyle}>
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0 flex-1">
              <h3
                style={{
                  fontWeight: 600,
                  color: 'var(--accent-danger)',
                  fontSize: '16px',
                  margin: '0 0 8px',
                }}
              >
                Delete Account
              </h3>
              {cancelDeletionSuccessMessage ? (
                <p
                  role="status"
                  style={{
                    color: 'var(--accent-green)',
                    fontSize: '14px',
                    lineHeight: 1.6,
                    margin: '0 0 12px',
                    fontWeight: 500,
                  }}
                >
                  {cancelDeletionSuccessMessage}
                </p>
              ) : null}
              <p
                style={{
                  color: 'var(--text-secondary)',
                  fontSize: '14px',
                  lineHeight: 1.6,
                  margin: 0,
                }}
              >
                Permanently delete your account and all associated data. This schedules deletion for 30
                days — you can cancel anytime by logging back in during that period.
              </p>
            </div>
            <div className="flex w-full shrink-0 lg:w-auto lg:justify-end">
              <button
                type="button"
                onClick={openDeleteModal}
                style={deleteAccountBtnStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--accent-danger)';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--accent-danger)';
                }}
              >
                Delete My Account
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteModalPortal}
    </div>
  );
}
